import type { AnswerV2 } from "../globals/types";

import type { Level, Question } from "./contents";

export type QuestionType = Question["type"];
type QuestionOf<T extends QuestionType> = Extract<Question, { type: T }>;

export type AnswerValue = string | number | string[];
export type Answer = { value?: AnswerValue; other?: string; comment?: string; files?: string[]; expiry?: string };
export type Answers = Record<string, Answer>;
export type AnswerUpdate = Answer | ((current: Answer | undefined) => Answer);

export const OTHER = "_other";

export const fromAnswersV2 = (list: AnswerV2[]): Answers =>
    Object.fromEntries(
        list.map((a) => [
            a.questionKey,
            {
                value: a.value as AnswerValue,
                other: a.other ?? undefined,
                comment: a.comment ?? undefined,
                files: a.fileNames,
                expiry: a.expiry ?? undefined,
            },
        ])
    );

export const withPrefill = (questions: Question[], answers: Answers): Answers => {
    const out = { ...answers };
    for (const question of questions) {
        const prefill = question.prefill as AnswerValue | null | undefined;
        if (prefill == null || isEmpty(prefill) || question.id in out) continue;
        out[question.id] = { value: prefill };
    }
    return out;
};

export const toAnswerV2Body = (answer: Answer) => ({
    value: answer.value ?? null,
    other: answer.other,
    fileNames: answer.files,
    comment: answer.comment,
    expiry: answer.expiry,
});

export const isEmpty = (value: AnswerValue | undefined) =>
    value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

export const answerIds = (value: AnswerValue | undefined): string[] => {
    if (isEmpty(value)) return [];
    return Array.isArray(value) ? value : [String(value)];
};

const rank: Record<Level, number> = { off: 0, optional: 1, required: 2 };

export const followUpLevel = (question: Question, answer: Answer | undefined, kind: "comment" | "file"): Level => {
    const base = (kind === "comment" ? question.commentLevel : question.fileUpload) ?? "off";
    const rules = "answerRules" in question ? (question.answerRules ?? {}) : {};
    return answerIds(answer?.value).reduce<Level>((level, id) => {
        const rule = rules[id]?.[kind] ?? "off";
        return rank[rule] > rank[level] ? rule : level;
    }, base);
};

export type InputProps<T extends QuestionType> = {
    question: QuestionOf<T>;
    answer: Answer | undefined;
    onChange: (answer: AnswerUpdate) => void;
    error: boolean;
    compact?: boolean;
};
